'use client';

import React from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';

/* ------------------------------------------------------------------------- */

function Container({children, className = ''}) {
  return (
    <div className={`mx-auto w-full max-w-[1200px] px-6 md:px-10 ${className}`}>
      {children}
    </div>
  );
}

const HELPFUL_HREFS = [
  '/templates/',
  '/features/',
  '/pricing/',
  '/request-demo/'
];

/* ------------------------------------------------------------------------- */

function SectionHero() {
  const t = useTranslations('Pages.NotFound');
  return (
    <section className="bg-background pt-32 pb-16 md:pt-40 md:pb-20">
      <Container>
        <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
          <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
            {t('meta.leadLabel')}
          </span>
          <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
          <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
            {t('meta.trailLabel')}
          </span>
        </Reveal>
        <Reveal>
          <p className="font-mono text-[8.5rem] leading-none tracking-tight text-foreground/[0.06] md:text-[14rem]">
            404
          </p>
        </Reveal>
        <Reveal delay={0.06}>
          <h1 className="headline-hero -mt-6 max-w-[22ch] text-foreground md:-mt-10">
            {t('hero.headline')}
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="lead mt-7 max-w-[58ch]">
            {t('hero.lead')}
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link href="/" className="cta-pill-brand">
              {t('hero.primaryCta')}
              <span aria-hidden>&rarr;</span>
            </Link>
            <Link href="/request-demo/" className="cta-text">
              {t('hero.secondaryCta')}
              <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function SectionLinks() {
  const t = useTranslations('Pages.NotFound');
  const items = t.raw('links.items');
  return (
    <section className="bg-background pt-12 pb-32 md:pt-16">
      <Container>
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-foreground-subtle">
            {t('links.eyebrow')}
          </p>
        </Reveal>
        <RevealStagger
          as="ul"
          mode="clone"
          className="mt-8 grid gap-px overflow-hidden rounded-[var(--radius-lg)] bg-border md:grid-cols-2 lg:grid-cols-3"
          gap={0.04}
          initialDelay={0.08}
        >
          {items.map((h, i) => {
            const href = HELPFUL_HREFS[i];
            return (
              <li key={href} className="bg-background">
                <Link
                  href={href}
                  className="group block h-full p-7 transition-colors hover:bg-foreground/[0.02]"
                >
                  <p className="font-display text-lg tracking-tight text-foreground md:text-xl">
                    {h.label}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-subtle">
                    {h.body}
                  </p>
                  <span className="cta-text-brand mt-6 inline-flex text-sm">
                    {t('links.openCta')}
                    <span aria-hidden>&rarr;</span>
                  </span>
                </Link>
              </li>
            );
          })}
        </RevealStagger>
      </Container>
    </section>
  );
}

/* ------------------------------------------------------------------------- */

export default function NotFoundV2() {
  return (
    <>
      <SectionHero />
      <SectionLinks />
    </>
  );
}
